import {ArrayUtils, ConfigObjectType} from '@wallaby-ui/core';
import {
  AlertCriteriaChip,
  AlertCriteriaChipList,
  AlertCriteriaChipVariant,
  AlertCriteriaRenderer,
  AlertEpisodeTooltipDto,
  AlertGenericCriteriaChips,
  SelectedFilter,
  isSelectedFilterVisible,
  parseFilterData,
  useFormatters,
} from '@wallaby-ui/document';
import React, {useCallback, useMemo} from 'react';

import {withOccurrenceDefaults} from '../hospitalizedPatientsFilters';
import {
  buildHospitalizedPatientsSelectedFilters,
  hospitalizedPatientsTableFiltersForChips,
  useHospitalizedPatientsChipLookups,
} from '../hospitalizedPatientsSelectedFilters';
import {
  buildPhRecommendationSelectedFilters,
  phRecommendationTableFiltersForChips,
  usePhRecommendationChipLookups,
} from '../phRecommendationSelectedFilters';

/** The saved-filter components with alert-eligible lists in MERIE and their chip builders. */
export const HOSPITALIZATION_LIST_COMPONENT = 'hospitalizationList';
export const PHARMACOTHERAPY_RECOMMENDATION_LIST_COMPONENT = 'pharmacotherapyRecommendationList';

type ChipsProps = {config: ConfigObjectType; filterData: AlertEpisodeTooltipDto['filterData']; variant?: AlertCriteriaChipVariant};

/** The visible chips of a page's "Active filters", as the alert tooltips show them. */
function toCriteriaChips(selectedFilters: SelectedFilter[], tableFilters: Record<string, any>): AlertCriteriaChip[] {
  return selectedFilters
    .filter((v) => isSelectedFilterVisible(v, tableFilters))
    .map((v) => ({key: ArrayUtils.asArray(v.name).join('+'), label: v.label, value: v.value ?? ''}));
}

/** The criteria of a hospitalizationList saved filter, exactly as the hospitalizations list shows them as chips. */
const HospitalizationCriteriaChips: React.FC<ChipsProps> = ({config, filterData, variant}) => {
  const formatters = useFormatters();
  const lookups = useHospitalizedPatientsChipLookups(config);
  const chips = useMemo(() => {
    const patients = withOccurrenceDefaults(parseFilterData(filterData));
    return toCriteriaChips(
      buildHospitalizedPatientsSelectedFilters(patients, formatters, lookups),
      hospitalizedPatientsTableFiltersForChips(patients)
    );
  }, [filterData, formatters, lookups]);
  return <AlertCriteriaChipList chips={chips} variant={variant} />;
};

/** The criteria of a pharmacotherapyRecommendationList saved filter, as the recommendation list shows them. */
const PhRecommendationCriteriaChips: React.FC<ChipsProps> = ({config, filterData, variant}) => {
  const formatters = useFormatters();
  const lookups = usePhRecommendationChipLookups(config);
  const chips = useMemo(() => {
    const recommendation = parseFilterData(filterData);
    return toCriteriaChips(
      buildPhRecommendationSelectedFilters(recommendation, formatters, lookups),
      phRecommendationTableFiltersForChips(recommendation)
    );
  }, [filterData, formatters, lookups]);
  return <AlertCriteriaChipList chips={chips} variant={variant} />;
};

/**
 * The criteria renderer of MERIE's alert surfaces: the chips of an alert definition read exactly like the
 * "Active filters" of the list the filter was saved on, built by the same code and the same code lists.
 * The component mounts only while a tooltip is open, so the lookups load on the first hover and stay cached.
 */
export function useMerieAlertCriteriaRenderer(config: ConfigObjectType): AlertCriteriaRenderer {
  return useCallback<AlertCriteriaRenderer>(
    (filterComponent, filterData, options) => {
      const variant = options?.variant;
      if (filterComponent === HOSPITALIZATION_LIST_COMPONENT)
        return <HospitalizationCriteriaChips config={config} filterData={filterData} variant={variant} />;
      if (filterComponent === PHARMACOTHERAPY_RECOMMENDATION_LIST_COMPONENT) {
        return <PhRecommendationCriteriaChips config={config} filterData={filterData} variant={variant} />;
      }
      return <AlertGenericCriteriaChips filterComponent={filterComponent} filterData={filterData} variant={variant} />;
    },
    [config]
  );
}
